import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Modal,
  IconButton,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { fetchTableData } from "../../../api/tableView";
import debounce from "lodash/debounce";
import InfoIcon from "@mui/icons-material/Info";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { Slide } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import DevicesIcon from "@mui/icons-material/Devices";
import StoreIcon from "@mui/icons-material/Store";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { fetchProductDetails } from "api/productDetails";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const TableView = () => {
  const { storeId, filter } = useParams();
  // const store_id = localStorage.getItem("analysisStoreId");
  const selectedDate = useSelector((state) => state.customization.selectedDate);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState(filter ? filter : "ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // eslint-disable-next-line
  const abortControllerRef = useRef(null);
  const loaderRef = useRef(null);
  const [startDate, setStartDate] = useState(selectedDate || new Date());
  const [endDate, setEndDate] = useState(selectedDate || new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  const getFilterParams = (status) => {
    switch (status) {
      case "EXPIRED":
        return { expired: true, almost_expired: false };
      case "NEAR EXPIRY":
        return { expired: false, almost_expired: true };
      default:
        return { expired: false, almost_expired: false };
    }
  };

  const fetchInitialData = useCallback(async () => {
    try {
      // if (abortControllerRef.current) {
      //   abortControllerRef.current.abort();
      // }

      setLoading(true);
      const filterParams = getFilterParams(filterStatus);

      const response = await fetchTableData({
        source_id: storeId,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        search_query: searchQuery,
        page: 1,
        ...filterParams,
      });

      console.log("filterParams", filterParams);

      const newData = Array.isArray(response.data)
        ? response.data
        : response.data.data
        ? response.data.data
        : [];

      setTableData(newData);
      setPage(response?.page || 1);
      setHasMore(
        response?.total_pages !== undefined
          ? (response?.page || 1) < response.total_pages
          : newData.length > 0
      );
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request cancelled");
      } else {
        console.error("Error fetching table data:", error);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [filterStatus, startDate, endDate]);

  const handleViewDetails = async (envId) => {
    // setSelectedProduct(null);
    setIsModalOpen(true);
    setLoadingImage(true);
    setCurrentSlide(0);

    try {
      const productDetails = await fetchProductDetails(envId);
      // console.log("prod", productDetails);
      if (productDetails) {
        // console.log("Product Images:", productDetails.images);
        setSelectedProduct(productDetails);
      }
    } catch (error) {
      // console.error("Error loading product details:", error);
    } finally {
      setLoadingImage(false);
    }
  };

  const handleNextImage = async () => {
    const series = tableData.map((item) => item.env_id);
    const currentData = selectedProduct?.env_id;
    const indexCurrentData = series.indexOf(currentData);
    const len = tableData.length;
    if (indexCurrentData === -1) return;

    const atEnd = indexCurrentData === len - 1;

    if (atEnd) {
      if (hasMore) {
        // Try to load the next page; navigate to the first newly loaded item if available
        const loaded = await handleLoadMore();
        if (Array.isArray(loaded) && loaded.length > 0) {
          const nextEnvId = loaded[0]?.env_id;
          if (nextEnvId) {
            handleViewDetails(nextEnvId);
            return;
          }
        }
      }
      // Last product: button is disabled, nothing to navigate to
      return;
    }

    const nextIndex = indexCurrentData + 1;
    handleViewDetails(series[nextIndex]);
  };

  const currentProductIndex = tableData.findIndex(
    (item) => item.env_id === selectedProduct?.env_id
  );
  const isLastProduct =
    !hasMore &&
    tableData.length > 0 &&
    currentProductIndex === tableData.length - 1;
  const isFirstProduct = currentProductIndex <= 0;

  const handlePrevImage = () => {
    if (currentProductIndex <= 0) return;
    handleViewDetails(tableData[currentProductIndex - 1].env_id);
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    fade: true,
    waitForAnimate: false,
    arrows: false,
    beforeChange: (current, next) => setCurrentSlide(next),
  };

  const slideArrowSx = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    bgcolor: "rgba(255, 255, 255, 0.85)",
    boxShadow: 2,
    "&:hover": { bgcolor: "#fff" },
  };

  // Function to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const fetchMoreData = useCallback(
    async (nextPage) => {
      try {
        setLoading(true);
        const filterParams = getFilterParams(filterStatus);

        const response = await fetchTableData({
          // store_id: storeId,
          source_id: storeId,
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
          search_query: searchQuery,
          page: nextPage,
          ...filterParams,
        });

        // console.log("Load More API Response:", response.data);

        const newData = Array.isArray(response.data)
          ? response.data
          : response.data.data
          ? response.data.data
          : [];

        setTableData((prevData) => [...prevData, ...newData]);
        setHasMore(
          response?.total_pages !== undefined
            ? (response?.page || nextPage) < response.total_pages
            : newData.length > 0
        );
        return newData; // return newly loaded items
      } catch (error) {
        // console.error("Error fetching more data:", error);
        setPage((prev) => prev - 1);
        return [];
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line
    [filterStatus, startDate, endDate, searchQuery]
  );

  // Debounced search function
  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 500),
    []
  );

  useEffect(() => {
    fetchInitialData();

    // return () => {
    //   if (abortControllerRef.current) {
    //     abortControllerRef.current.abort();
    //   }
    // };
  }, [fetchInitialData]);

  useEffect(() => {
    setStartDate(selectedDate || new Date());
    setEndDate(selectedDate || new Date());
  }, [selectedDate]);

  useEffect(() => {
    if (filter && ["ALL", "EXPIRED", "NEAR EXPIRY"].includes(filter)) {
      setFilterStatus(filter);
    }
  }, [filter]);

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterStatus(newFilter);
    }
  };

  const handleLoadMore = useCallback(async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    const loaded = await fetchMoreData(nextPage);
    return loaded;
  }, [page, fetchMoreData]);

  const handleSearchChange = (event) => {
    debouncedSearch(event.target.value);
  };

  // Infinite scroll: fetch next page when the loader comes into view
  useEffect(() => {
    if (loading || !hasMore) return;

    const node = loaderRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [loading, hasMore, handleLoadMore]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(tableData)) {
      // console.warn("tableData is not an array:", tableData);
      return [];
    }

    if (!searchQuery) {
      return tableData;
    }

    const searchLower = searchQuery.toLowerCase().trim();

    return tableData.filter((row) => {
      // Convert all values to strings and handle null/undefined
      const batchNo = (row.batch_no || "").toString().toLowerCase();
      const ean = (row.ean || "").toString().toLowerCase();
      const mfgDate = (row.mfg_date || "").toString().toLowerCase();
      const expiryDate = (row.expiry_date || "").toString().toLowerCase();
      const mrp = (row.mrp || "").toString().toLowerCase();

      return (
        batchNo.includes(searchLower) ||
        ean.includes(searchLower) ||
        mfgDate.includes(searchLower) ||
        expiryDate.includes(searchLower) ||
        mrp.includes(searchLower)
      );
    });
  }, [tableData, searchQuery]);

  const formatCustomDate = (dateString) => {
    try {
      if (!dateString) {
        return "-"; // Return dash for null/undefined/empty values
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-"; // Return dash for invalid dates
      }
      return format(date, "dd-MM-yyyy");
    } catch (error) {
      // console.warn("Date parsing error:", error);
      return "-";
    }
  };

  const exportToCSV = useCallback(() => {
    try {
      // console.log("Exporting data:", filteredData);

      const headers = [
        "Timestamp",
        "EAN",
        "Batch No",
        "MRP",
        "Mfg Date",
        "Expiry Date",
        "Status",
      ];

      const csvData = filteredData.map((row) => [
        format(new Date(row.timestamp), "dd/MM/yyyy HH:mm:ss"),
        row.ean || "",
        row.batch_no || "",
        row.mrp || "",
        formatCustomDate(row.mfg_date),
        formatCustomDate(row.expiry_date),
        row.expired ? "Expired" : row.almost_expired ? "Near Expiry" : "Ok",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.join(",")),
      ].join("\n");

      // console.log("CSV Content:", csvContent); // Debug log

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = `product_data_${format(
        selectedDate || new Date(),
        "yyyy-MM-dd"
      )}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // console.log("CSV Export completed");
    } catch (error) {
      // console.error("Error exporting CSV:", error);
    }
  }, [filteredData, selectedDate]);

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}>
          <TextField
            size="small"
            placeholder="Search products..."
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { size: "small" } }}
                maxDate={endDate}
                format="dd/MM/yyyy"
              />
              <Typography variant="body2" color="text.secondary">
                to
              </Typography>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { size: "small" } }}
                minDate={startDate}
                format="dd/MM/yyyy"
              />
            </Box>
          </LocalizationProvider>
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={handleFilterChange}
            size="small">
            <ToggleButton value="ALL">All</ToggleButton>
            <ToggleButton value="EXPIRED">Expired</ToggleButton>
            <ToggleButton value="NEAR EXPIRY">Near Expiry</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={exportToCSV}
          color="primary"
          size="small"
          disabled={filteredData.length === 0}
          sx={{
            borderRadius: 1,
            height: "36px",
            bgcolor: "background.paper",
          }}>
          Export CSV
        </Button>
      </Box>

      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-label="product table">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>EAN</TableCell>
                <TableCell>Batch No</TableCell>
                <TableCell>MRP</TableCell>
                <TableCell>Mfg Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell>
                      {format(new Date(row.timestamp), "dd/MM/yyyy HH:mm:ss")}
                    </TableCell>
                    <TableCell>{row.ean}</TableCell>
                    <TableCell>{row.batch_no}</TableCell>
                    <TableCell>₹{row.mrp}</TableCell>
                    <TableCell>{formatCustomDate(row.mfg_date)}</TableCell>
                    <TableCell>{formatCustomDate(row.expiry_date)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          display: "inline-block",
                          bgcolor: (theme) =>
                            theme.palette[
                              row.expired
                                ? "error"
                                : row.almost_expired
                                ? "warning"
                                : "success"
                            ].light,
                          color: (theme) =>
                            theme.palette[
                              row.expired
                                ? "error"
                                : row.almost_expired
                                ? "warning"
                                : "success"
                            ].dark,
                        }}>
                        {row.expired
                          ? "Expired"
                          : row.almost_expired
                          ? "Near Expiry"
                          : "Ok"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(row?.env_id)}
                        color="primary">
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom>
                        No Data Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {`No products found for ${format(
                          selectedDate || new Date(),
                          "dd/MM/yyyy"
                        )}`}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <Modal
              open={isModalOpen}
              onClose={handleCloseModal}
              aria-labelledby="product-details-modal"
              aria-describedby="product-details-description">
              <Slide
                direction="left"
                in={isModalOpen}
                timeout={500}
                easing={{ enter: "ease-in" }}>
                <Box
                  sx={{
                    position: "fixed",
                    top: { xs: "16px", sm: "64px" },
                    left: { xs: "16px", sm: "240px" },
                    right: "16px",
                    bottom: "16px",
                    bgcolor: "background.paper",
                    p: { xs: 2, sm: 4 },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 3,
                    overflowY: "auto",
                    zIndex: 1500,
                    width: {
                      xs: "calc(100% - 32px)",
                      sm: "calc(100% - 240px)",
                    },
                  }}>
                  <IconButton
                    onClick={handlePrevImage}
                    disabled={isFirstProduct || loadingImage}
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "black",
                      "&.Mui-disabled": { color: "action.disabled" },
                      width: 40,
                      height: 40,
                      transition: "all 0.3s ease-in-out",
                    }}>
                    <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                  {/* Left: Product Image */}
                  <Box
                    sx={{
                      flex: { xs: "none", sm: 3 },
                      minWidth: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: "grey.100",
                      borderRadius: 2,
                      p: 2,
                      mx: { xs: 0, sm: 4 },
                      minHeight: "200px",
                      height: { xs: "auto", sm: "100%" },
                      boxSizing: "border-box",
                      position: "relative",
                    }}>
                    {loadingImage ? (
                      <CircularProgress />
                    ) : selectedProduct?.images?.length >= 1 ? (
                      <Box sx={{ width: "100%", position: "relative" }}>
                        {selectedProduct.images.length > 1 && (
                          <>
                            <IconButton
                              aria-label="previous image"
                              onClick={() => sliderRef.current?.slickPrev()}
                              sx={{ ...slideArrowSx, left: 8 }}>
                              <ChevronLeftIcon />
                            </IconButton>
                            <IconButton
                              aria-label="next image"
                              onClick={() => sliderRef.current?.slickNext()}
                              sx={{ ...slideArrowSx, right: 8 }}>
                              <ChevronRightIcon />
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                color: "#fff",
                                bgcolor: "rgba(0, 0, 0, 0.6)",
                              }}>
                              {currentSlide + 1} / {selectedProduct.images.length}
                            </Typography>
                          </>
                        )}
                        <Slider ref={sliderRef} {...settings}>
                          {selectedProduct.images.map((image, index) => {
                            return (
                              <div key={index}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: {
                                      xs: "50vh",
                                      sm: "calc(100vh - 230px)",
                                    },
                                  }}>
                                  <Box
                                    component="img"
                                    src={image.sasUrl}
                                    alt="no_image"
                                    sx={{
                                      maxWidth: "100%",
                                      maxHeight: "100%",
                                      width: "auto",
                                      height: "auto",
                                      objectFit: "contain",
                                      borderRadius: 1.5,
                                      boxShadow: 3,
                                    }}
                                  />
                                </Box>
                              </div>
                            );
                          })}
                        </Slider>
                      </Box>
                    ) : (
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}>
                        No Images Available
                      </Typography>
                    )}
                  </Box>

                  {/* Right: Product Details */}
                  <Box
                    sx={{
                      flex: { xs: "none", sm: 2 },
                      position: "relative",
                      pr: { xs: 0, sm: 5 },
                    }}>
                    <IconButton
                      onClick={handleCloseModal}
                      sx={{
                        position: "absolute",
                        top: { xs: 4, sm: -12 }, // Adjust close button position for smaller screens
                        right: 4,
                        color: "text.primary",
                      }}>
                      <CloseIcon />
                    </IconButton>

                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" }, // Responsive font size
                      }}>
                      {selectedProduct?.product_name || "Product Name"}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{
                        color: "text.secondary",
                        fontSize: { xs: "0.875rem", sm: "1rem" }, // Responsive font size
                      }}>
                      Product Description
                    </Typography>
                    <hr />
                    {selectedProduct?.ean &&
                      selectedProduct?.ean !== "" &&
                      !isNaN(selectedProduct.ean) && (
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                            mt: 2,
                            fontSize: "1rem",
                            fontWeight: "500",
                          }}>
                          <QrCodeIcon
                            fontSize="small"
                            sx={{ color: "#1976d2" }}
                          />
                          <strong>EAN:</strong> {selectedProduct.ean}
                        </Typography>
                      )}

                    {selectedProduct?.batchNo &&
                      selectedProduct?.batchNo !== "N/A" && (
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                            mt: 2,
                            fontSize: "1rem",
                            fontWeight: "500",
                          }}>
                          <InfoIcon
                            fontSize="small"
                            sx={{ color: "#1976d2" }}
                          />{" "}
                          {/* Blue */}
                          <strong>Batch No:</strong>
                          {selectedProduct?.batchNo}
                        </Typography>
                      )}

                    {selectedProduct?.mrp &&
                      selectedProduct?.mrp !== "N/A" &&
                      !isNaN(selectedProduct.mrp) && (
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                            mt: 2,
                            fontSize: "1rem",
                            fontWeight: "500",
                          }}>
                          <CurrencyRupeeIcon
                            fontSize="small"
                            sx={{ color: "#388e3c" }}
                          />{" "}
                          {/* Green */}
                          <strong>MRP:</strong> ₹{selectedProduct?.mrp}
                        </Typography>
                      )}

                    {selectedProduct?.mfgDate && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                          mt: 2,
                          fontSize: "1rem",
                          fontWeight: "500",
                        }}>
                        <CalendarTodayIcon
                          fontSize="small"
                          sx={{ color: "#f57c00" }}
                        />{" "}
                        {/* Orange */}
                        <strong>Manufacturing Date:</strong>{" "}
                        {formatCustomDate(selectedProduct?.mfgDate)}
                      </Typography>
                    )}

                    {selectedProduct?.expiryDate && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                          mt: 2,
                          fontSize: "1rem",
                          fontWeight: "500",
                        }}>
                        <EventAvailableIcon
                          fontSize="small"
                          sx={{ color: "#d32f2f" }}
                        />
                        <strong>Expiry Date:</strong>{" "}
                        {formatCustomDate(selectedProduct?.expiryDate)}
                      </Typography>
                    )}

                    {selectedProduct?.userName && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                          mt: 2,
                          fontSize: "1rem",
                          fontWeight: "500",
                        }}>
                        <PersonIcon
                          fontSize="small"
                          sx={{ color: "#4caf50" }}
                        />{" "}
                        {/* Green */}
                        <strong>Captured By:</strong>
                        {selectedProduct?.userName}
                      </Typography>
                    )}

                    {selectedProduct?.timeStamps && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                          mt: 2,
                          fontSize: "1rem",
                          fontWeight: "500",
                        }}>
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{ color: "#9c27b0" }}
                        />{" "}
                        {/* Purple */}
                        <strong>Captured At:</strong>{" "}
                        {formatCustomDate(selectedProduct?.timeStamps)}
                      </Typography>
                    )}

                    {selectedProduct?.deviceName && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                          mt: 2,
                          fontSize: "1rem",
                          fontWeight: "500",
                        }}>
                        <DevicesIcon
                          fontSize="small"
                          sx={{ color: "#1976d2" }}
                        />{" "}
                        {/* Blue */}
                        <strong>Device Name:</strong>
                        {selectedProduct?.deviceName}
                      </Typography>
                    )}

                    {selectedProduct?.storeId && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                          mt: 2,
                          fontSize: "1rem",
                          fontWeight: "500",
                        }}>
                        <StoreIcon fontSize="small" sx={{ color: "#ff9800" }} />{" "}
                        {/* Orange */}
                        <strong>Store ID:</strong> {selectedProduct.storeId}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    onClick={handleNextImage}
                    disabled={isLastProduct || loading || loadingImage}
                    sx={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "black",
                      "&.Mui-disabled": { color: "action.disabled" },
                      width: 40,
                      height: 40,
                      transition: "all 0.3s ease-in-out",
                    }}>
                    <KeyboardDoubleArrowRightIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </Box>
              </Slide>
            </Modal>
          </Table>
        </TableContainer>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && hasMore && tableData.length > 0 && (
          <Box ref={loaderRef} sx={{ height: 40 }} />
        )}
      </Paper>
    </Box>
  );
};

export default TableView;
